import { format, addMinutes, isBefore, isAfter, isEqual, parse, startOfDay, endOfDay } from 'date-fns';

/**
 * Génère les créneaux disponibles pour une date donnée
 * @param {Date} date - La date sélectionnée
 * @param {Object} openingHours - Les horaires du jour { open: "09:00", close: "19:00", ... }
 * @param {Array} existingBookings - Les réservations existantes du jour
 * @param {number} serviceDuration - Durée du service demandé en minutes
 * @returns {Array} Liste des horaires disponibles ["09:00", "09:30", ...]
 */
export const calculateFreeSlots = (date, openingHours, existingBookings, serviceDuration = 60) => {
    if (!openingHours || openingHours.is_closed) return [];

    const slots = [];
    const step = 30; // Pas de temps entre chaque créneau (30 min)

    // Convertir les heures string "09:00" en objets Date basés sur la date sélectionnée
    const openTime = parseTime(date, openingHours.open);
    const closeTime = parseTime(date, openingHours.close);

    // Gérer la pause déjeuner si elle existe
    let breakStart = null;
    let breakEnd = null;
    if (openingHours.break_start && openingHours.break_end) {
        breakStart = parseTime(date, openingHours.break_start);
        breakEnd = parseTime(date, openingHours.break_end);
    }

    let currentTime = openTime;

    // Boucle de l'ouverture à la fermeture
    while (isBefore(currentTime, closeTime)) {
        // Calculer la fin du service potentiel
        const potentialEndTime = addMinutes(currentTime, serviceDuration);

        // Si le service finit après la fermeture, stop
        if (isAfter(potentialEndTime, closeTime)) break;

        // Vérifier si le créneau tombe pendant la pause
        let isInBreak = false;
        if (breakStart && breakEnd) {
            // Si le début est pendant la pause OU la fin est pendant la pause
            if (
                (isAfter(currentTime, breakStart) && isBefore(currentTime, breakEnd)) || // Commence pendant la pause
                (isAfter(potentialEndTime, breakStart) && isBefore(potentialEndTime, breakEnd)) || // Finit pendant la pause
                (isEqual(currentTime, breakStart)) // Pile au début de la pause
            ) {
                isInBreak = true;
            }
        }

        // Vérifier si collision avec un RDV existant
        let isBooked = false;
        for (const booking of existingBookings) {
            const bookingStart = new Date(booking.date);
            // On estime la fin du booking existant (il faudrait avoir booking.duration, sinon on assume 60min par défaut ou on utilise booking.service.duration)
            // Pour simplifier ici, on utilise la durée stockée dans la réservation ou une valeur par défaut
            const duration = booking.expand?.service?.duration || 60;
            const bookingEnd = addMinutes(bookingStart, duration);

            // Chevauchement de périodes
            // (StartA < EndB) and (EndA > StartB)
            if (isBefore(currentTime, bookingEnd) && isAfter(potentialEndTime, bookingStart)) {
                isBooked = true;
                break;
            }
        }

        // Si pas de pause et pas de RDV, on ajoute
        if (!isInBreak && !isBooked) {
            // Ne pas proposer de créneaux passés si c'est aujourd'hui
            const now = new Date();
            if (isAfter(currentTime, now)) {
                slots.push(format(currentTime, 'HH:mm'));
            }
        }

        // Avancer au prochain créneau
        currentTime = addMinutes(currentTime, step);
    }

    return slots;
};

// Helper pour parser "HH:mm" sur la date donnée
const parseTime = (date, timeString) => {
    return parse(timeString, 'HH:mm', date);
};
